/**
 * Push-Benachrichtigungen an- und abmelden.
 *
 * Der heikle Teil ist iOS: dort gibt es Web Push erst ab 16.4 und nur, wenn
 * die App über „Zum Home-Bildschirm" installiert wurde. Im Safari-Tab fehlt
 * `PushManager` schlicht — deshalb erklärt der Baustein das, statt einen
 * Knopf anzubieten, der nichts tut.
 */
import { useCallback, useEffect, useState } from 'react'
import { api, ApiFehler } from '../api/client.js'
import { Amtsknopf, Fehlermeldung } from './Bausteine.js'

interface Schluessel {
  moeglich: boolean
  publicKey: string
  angemeldet: boolean
}

type Lage =
  | 'pruefe'
  | 'server_aus' // Der Server hat keine Schlüssel.
  | 'geraet_kann_nicht' // Kein PushManager — alter Browser.
  | 'nur_installiert' // iOS im Tab: erst zum Home-Bildschirm.
  | 'verweigert' // Der Nutzer hat abgelehnt.
  | 'aus'
  | 'an'

/** Base64url aus der Serverantwort in das Format, das der Browser will. */
function alsBytes(base64: string): ArrayBuffer {
  const gefuellt = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const roh = atob(gefuellt)
  const bytes = new Uint8Array(roh.length)
  for (let i = 0; i < roh.length; i += 1) bytes[i] = roh.charCodeAt(i)
  return bytes.buffer
}

/** Läuft die App als installierte PWA? Auf iOS entscheidet das über Push. */
function istInstalliert(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari kennt display-mode nicht zuverlässig, dafür diese Eigenheit.
    (navigator as { standalone?: boolean }).standalone === true
  )
}

export function PushSchalter() {
  const [lage, setLage] = useState<Lage>('pruefe')
  const [schluessel, setSchluessel] = useState<Schluessel | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const pruefe = useCallback(async () => {
    try {
      const antwort = await api.get<Schluessel>('/api/push/schluessel')
      setSchluessel(antwort)

      if (!antwort.moeglich) return setLage('server_aus')
      if (!('serviceWorker' in navigator)) return setLage('geraet_kann_nicht')
      if (!('PushManager' in window)) {
        // iOS im Tab: PushManager fehlt, bis die App installiert ist.
        return setLage(istInstalliert() ? 'geraet_kann_nicht' : 'nur_installiert')
      }
      if (Notification.permission === 'denied') return setLage('verweigert')

      const registrierung = await navigator.serviceWorker.ready
      const vorhanden = await registrierung.pushManager.getSubscription()
      setLage(vorhanden ? 'an' : 'aus')
    } catch {
      setLage('server_aus')
    }
  }, [])

  useEffect(() => {
    void pruefe()
  }, [pruefe])

  async function meldeAn(): Promise<void> {
    if (!schluessel) return
    setLaeuft(true)
    setFehler(null)
    try {
      const erlaubnis = await Notification.requestPermission()
      if (erlaubnis !== 'granted') {
        setLage('verweigert')
        return
      }

      const registrierung = await navigator.serviceWorker.ready
      const vorhanden = await registrierung.pushManager.getSubscription()
      const abo =
        vorhanden ??
        (await registrierung.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: alsBytes(schluessel.publicKey),
        }))

      await api.post('/api/push/abo', abo.toJSON())
      setLage('an')
    } catch (f) {
      setFehler(
        f instanceof ApiFehler
          ? f.message
          : 'Die Anmeldung ist fehlgeschlagen. Ist die App vom Home-Bildschirm gestartet?',
      )
    } finally {
      setLaeuft(false)
    }
  }

  async function meldeAb(): Promise<void> {
    setLaeuft(true)
    setFehler(null)
    try {
      const registrierung = await navigator.serviceWorker.ready
      const abo = await registrierung.pushManager.getSubscription()
      if (abo) {
        await api.del('/api/push/abo', { endpoint: abo.endpoint })
        await abo.unsubscribe()
      }
      setLage('aus')
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Die Abmeldung ist fehlgeschlagen.')
    } finally {
      setLaeuft(false)
    }
  }

  async function probe(): Promise<void> {
    setLaeuft(true)
    setFehler(null)
    try {
      await api.post('/api/push/probe')
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Die Probe ging nicht raus.')
    } finally {
      setLaeuft(false)
    }
  }

  if (lage === 'pruefe' || lage === 'server_aus') return null

  return (
    <div className="border border-eiche bg-bank px-4 py-4">
      <p className="protokoll">Benachrichtigungen</p>

      {lage === 'nur_installiert' && (
        <p className="mt-2 text-sm text-akte/80">
          Auf dem iPhone gibt es Benachrichtigungen nur, wenn die App über <em>Teilen → Zum
          Home-Bildschirm</em> installiert ist. Von dort aus geöffnet, erscheint hier ein Knopf.
        </p>
      )}

      {lage === 'geraet_kann_nicht' && (
        <p className="mt-2 text-sm text-akte/80">
          Dieser Browser kann keine Benachrichtigungen. Die App funktioniert trotzdem — der Alarm
          erscheint dann nur, solange sie offen ist.
        </p>
      )}

      {lage === 'verweigert' && (
        <p className="mt-2 text-sm text-akte/80">
          Benachrichtigungen sind für diese Seite abgelehnt. Das lässt sich nur in den
          Einstellungen des Browsers zurücknehmen — danach hier erneut anmelden.
        </p>
      )}

      {lage === 'aus' && (
        <>
          <p className="mt-2 text-sm text-akte/80">
            Damit erreicht dich der Schnapsuhr-Alarm auch bei gesperrtem Handy — ebenso
            Herausforderungen und Vorladungen in den Zeugenstand.
          </p>
          <div className="mt-3">
            <Amtsknopf onClick={() => void meldeAn()} disabled={laeuft}>
              {laeuft ? 'Wird angemeldet …' : 'Benachrichtigungen einschalten'}
            </Amtsknopf>
          </div>
        </>
      )}

      {lage === 'an' && (
        <>
          <p className="mt-2 text-sm text-akte/80">
            Dieses Gerät ist angemeldet. Die Kammer meldet sich, wenn es etwas zu vollstrecken gibt.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Amtsknopf variante="leder" onClick={() => void probe()} disabled={laeuft}>
              Probe senden
            </Amtsknopf>
            <Amtsknopf variante="leder" onClick={() => void meldeAb()} disabled={laeuft}>
              Abschalten
            </Amtsknopf>
          </div>
        </>
      )}

      <Fehlermeldung text={fehler} />
    </div>
  )
}
